import { ContentSafetyButton } from './AccountSafety';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DateTime } from 'luxon';
import { apiCallJson } from '../utils/auth';
import styles from './ClientMemoList.module.css';

// React Query 훅 (향후 마이그레이션용) - 현재 미사용
// import { useApiQuery, useApiMutation } from '../lib/useApiQuery';

const TEXT = {
  noMemos: { ko: '메모가 없습니다.', en: 'No memos yet.', fr: 'Aucun mémo pour le moment.' },
  loadMore: { ko: '더 보기', en: 'Load more', fr: 'Voir plus' },
  loading: { ko: '로딩 중...', en: 'Loading...', fr: 'Chargement...' },
  deleteConfirm: { ko: '메모를 삭제하시겠습니까?', en: 'Delete this memo?', fr: 'Supprimer ce mémo ?' },
  editPlaceholder: { ko: '메모를 수정하세요...', en: 'Edit memo...', fr: 'Modifier le mémo...' },
  save: { ko: '저장', en: 'Save', fr: 'Enregistrer' },
  cancel: { ko: '취소', en: 'Cancel', fr: 'Annuler' },
  delete: { ko: '삭제', en: 'Delete', fr: 'Supprimer' },
  edit: { ko: '수정', en: 'Edit', fr: 'Modifier' },
  yesterday: { ko: '어제', en: 'Yesterday', fr: 'Hier' }
};

const t = (key, lang) => TEXT[key]?.[lang] || TEXT[key]?.['ko'] || key;

export default function ClientMemoList({ userInfo, onMemoUpdate, onShowModal, currentTheme }) {
  const [memos, setMemos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [saveLoading, setSaveLoading] = useState(null);
  const [clickedMemoId, setClickedMemoId] = useState(null);

  const lang = userInfo?.preferred_language || 'ko';
  const observer = useRef();
  const pageRef = useRef(1);
  const loadingRef = useRef(false);

  const loadMemos = useCallback(async (reset = false) => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);
    try {
      const currentPage = reset ? 1 : pageRef.current;
      const response = await apiCallJson(`/api/client-memos?page=${currentPage}&limit=30`);

      if (!response) {
        return;
      }

      if (reset) {
        setMemos(response.memos || []);
        pageRef.current = 2;
      } else {
        setMemos(prev => [...prev, ...(response.memos || [])]);
        pageRef.current += 1;
      }

      setHasMore(response.pagination?.hasMore || false);
    } catch (error) {
      console.error('메모 로드 실패:', error);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  const lastMemoRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMemos();
      }
    });
    if (node) observer.current.observe(node);
  }, [hasMore, loadMemos, loading]);

  useEffect(() => {
    loadMemos(true);
    const refresh=()=>{setMemos([]);loadMemos(true);};
    window.addEventListener('safety-changed',refresh);
    return ()=>window.removeEventListener('safety-changed',refresh);
  }, [loadMemos]);

  const handleMemoClick = (memoId) => {
    setClickedMemoId(clickedMemoId === memoId ? null : memoId);
  };

  const handleEdit = (memo) => {
    setEditingId(memo.id);
    setEditContent(memo.content);
    setClickedMemoId(null); // 편집 모드로 전환 시 클릭 상태 해제
  };

  const handleSave = async (memoId) => {
    if (!editContent.trim()) return;

    setSaveLoading(memoId);
    try {
      const response = await apiCallJson(`/api/client-memos/${memoId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: editContent.trim() })
      });

      if (response) {
        setMemos(prev => prev.map(memo =>
          memo.id === memoId
            ? { ...memo, content: editContent.trim() }
            : memo
        ));
        setEditingId(null);
        setEditContent('');
        if (onMemoUpdate) onMemoUpdate();
      }
    } catch (error) {
      console.error('메모 수정 실패:', error);
    } finally {
      setSaveLoading(null);
    }
  };

  const handleDelete = async (memoId) => {
    if (!window.confirm(t('deleteConfirm', lang))) return;

    setDeleteLoading(memoId);
    try {
      const response = await apiCallJson(`/api/client-memos/${memoId}`, {
        method: 'DELETE'
      });

      if (response) {
        setMemos(prev => prev.filter(memo => memo.id !== memoId));
        if (onMemoUpdate) onMemoUpdate();
      }
    } catch (error) {
      console.error('메모 삭제 실패:', error);
    } finally {
      setDeleteLoading(null);
    }
  };

  const formatDate = (dateString) => {
    const date = DateTime.fromISO(dateString).setZone('Europe/Paris');
    const now = DateTime.now().setZone('Europe/Paris');

    if (date.hasSame(now, 'day')) {
      return date.toFormat('HH:mm');
    } else if (date.hasSame(now.minus({ days: 1 }), 'day')) {
      return t('yesterday', lang);
    } else {
      return date.toFormat('dd/MM HH:mm');
    }
  };

  if (memos.length === 0 && !loading) {
    return (
      <div className={styles.memoContainer} style={{ color: currentTheme?.inactiveColor }}>
        <div className={styles.noMemosText} style={{ color: currentTheme?.inactiveColor }}>
          {t('noMemos', lang)}
        </div>
        {/* 메모 작성 버튼 */}
        <div className={styles.addButtonContainer}>
          <button
            onClick={onShowModal}
            className={styles.addButton}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              boxShadow: 'none'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.memoContainer} style={{ color: currentTheme?.inactiveColor }}>
      {memos.map((memo, index) => {
        const isLast = index === memos.length - 1;
        const isEditing = editingId === memo.id;
        const isOwnMemo = memo.user_id === userInfo?.user_id;

        return (
          <div
            key={memo.id}
            ref={isLast ? lastMemoRef : null}
            className={styles.memoItem}
          >
            {isEditing ? (
              <div>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  placeholder={t('editPlaceholder', lang)}
                  className={styles.editTextarea}
                  maxLength={1000}
                />
                <div className={styles.editButtons}>
                  <button
                    onClick={() => handleSave(memo.id)}
                    disabled={saveLoading === memo.id}
                    className={styles.saveButton}
                  >
                    {saveLoading === memo.id ? t('loading', lang) : t('save', lang)}
                  </button>
                  <button
                    onClick={() => {
                      setEditingId(null);
                      setEditContent('');
                    }}
                    className={styles.cancelButton}
                  >
                    {t('cancel', lang)}
                  </button>
                </div>
              </div>
            ) : (
              <div
                className={styles.memoRow}
                onClick={() => handleMemoClick(memo.id)}
                style={{ cursor: 'pointer' }}
              >
                <div className={styles.memoLeftGroup}>
                  <span className={styles.memoUser}>{memo.user_name}</span>
                  <span className={styles.memoSeparator}> - </span>
                  <span className={styles.memoContent}>{memo.content}</span>
                </div>
                <div className={styles.memoRightGroup}>
                  <span className={styles.memoTime}>{formatDate(memo.created_at)}</span>
                  {!isOwnMemo && <ContentSafetyButton kind="memo" contentID={memo.id} authorID={memo.user_id} variant="icon" />}
                  {isOwnMemo && (
                    <div className={`${styles.memoActions} ${clickedMemoId === memo.id ? styles.show : ''}`}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(memo);
                        }}
                        className={styles.editIcon}
                        title={t('edit', lang)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(memo.id);
                        }}
                        disabled={deleteLoading === memo.id}
                        className={styles.deleteIcon}
                        title={t('delete', lang)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                          <polyline points="3,6 5,6 21,6"></polyline>
                          <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,2v2"></path>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {loading && (
        <div className={styles.loadingText} style={{ color: currentTheme?.inactiveColor }}>
          {t('loading', lang)}
        </div>
      )}

      {/* 메모 작성 버튼 */}
      <div className={styles.addButtonContainer}>
        <button
          onClick={onShowModal}
          className={styles.addButton}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none',
            boxShadow: 'none'
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </button>
      </div>
    </div>
  );
}
